// Define interfaces for type safety
interface ServiceDetail {
  description: string;
  benefits: string[];
  process: string[];
  duration: string;
  warranty: string;
  maintenance: string;
}

interface Translations {
  en: typeof englishTranslations;
  ar: typeof englishTranslations;
  ru: typeof englishTranslations;
}

const englishTranslations = {
  common: {
    about: 'About',
    services: 'Services',
    signIn: 'Sign In',
    logout: 'Logout',
    dashboard: 'Dashboard',
    manage: 'Manage',
    loading: 'Loading...',
    cancel: 'Cancel',
    remove: 'Remove',
    error: 'An error occurred',
    success: 'Success!',
    save: 'Save',
    bookNow: 'Book Now',
    english: 'English',
    arabic: 'العربية',
    russian: 'Русский',
    getStarted: 'Get Started Now',
    goToDashboard: 'Go to Dashboard',
    learnMore: 'Learn More',
    backTo: 'Back to',
    allServices: 'All Services',
    process: 'Process',
    benefits: 'Benefits',
    overview: 'Overview',
    duration: 'Duration',
    warranty: 'Warranty',
    maintenance: 'Maintenance',
    serviceDetails: 'Service Details'
  },
  profile: {
    myAccount: 'Profile',
    title: 'Profile Information',
    name: 'Name',
    dateOfBirth: 'Date of Birth',
    phoneNumbers: 'Phone Numbers',
    primary: 'Primary',
    secondary: 'Secondary',
    addSecondaryPhone: 'Add Secondary Phone Number',
    removeSecondaryPhone: 'Remove secondary phone',
    notSet: 'Not set',
    avatar: 'Profile Avatar',
    updateSuccess: 'Your profile has been updated successfully.'
  },
  auth: {
    login: {
      title: 'Sign In',
      email: 'Email',
      password: 'Password',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      signUp: 'Sign up',
      signingIn: 'Signing in...',
      resetInstructions: 'Check your email for password reset instructions'
    },
    register: {
      title: 'Create Account',
      firstName: 'First Name',
      lastName: 'Last Name',
      username: 'Username',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      haveAccount: 'Already have an account?',
      signIn: 'Sign in',
      creatingAccount: 'Creating account...',
      passwordsDoNotMatch: 'Passwords do not match',
      passwordTooWeak: 'Password is too weak',
      usernameTaken: 'Username is already taken',
      usernameRequirements: 'Username must be 5-10 characters long'
    }
  },
  home: {
    hero: {
      title: 'Your Trusted Auto Service Partner',
      subtitle: 'Professional car care and maintenance services in Dubai',
      cta: 'Explore Our Services'
    },
    steps: {
      title: 'How It Works',
      step1: {
        title: 'Step 1',
        heading: 'Register Your Vehicle',
        description: 'Create an account and register your vehicle with us by uploading your registration card.'
      },
      step2: {
        title: 'Step 2',
        heading: 'Choose Services',
        description: 'Browse our comprehensive range of services and select what you need.'
      },
      step3: {
        title: 'Step 3',
        heading: 'Get Service',
        description: 'Receive professional service from our expert team.'
      }
    },
    newsletter: {
      title: 'Stay Updated',
      subtitle: 'Subscribe to our newsletter for the latest news and offers',
      placeholder: 'Enter your email',
      button: 'Subscribe',
      success: 'Thank you for subscribing to our newsletter!'
    }
  },
  services: {
    title: 'Our Services',
    subtitle: 'Professional automotive services tailored to your needs',
    categories: {
      carCare: {
        title: 'Car Care',
        description: 'Premium car care services including PPF, ceramic coating, and detailing',
        services: [
          'PPF (Paint Protection Film)',
          'Ceramic Coating',
          'Window Tinting',
          'Detailing',
          'Color Wrapping',
          'Headlight Restoration',
          'Interior & Exterior Restoration'
        ]
      },
      bodyWorks: {
        title: 'Body Works',
        description: 'Professional body work services including painting and dent repair',
        services: [
          'Painting & Custom Paint Jobs',
          'Rim Repairing & Customization',
          'Dent Repairing',
          'Scratch Repairing & Buffing',
          'Car Body Parts Adjustments',
          'Rust Removal & Prevention'
        ]
      },
      mechanical: {
        title: 'Mechanical Works',
        description: 'Expert mechanical services for optimal vehicle performance',
        services: [
          'Transmission & Engine Overhaul',
          'Part Swaps & Installation',
          'Tuning & Performance Upgrades',
          'Suspension Repairs & Upgrades',
          'Brake System Repairs',
          'Cooling System Repairs',
          'Electrical Repairs'
        ]
      },
      service: {
        title: 'Service',
        description: 'Regular maintenance and service operations',
        services: [
          'Oil Change',
          'Tyre Change & Rotation',
          'Brake Pads Replacement',
          'Diagnostic Inspection',
          'Filter Replacement',
          'Fluids Top-Up',
          'AC Service & Gas Refilling'
        ]
      },
      parts: {
        title: 'Spare Parts',
        description: 'Quality genuine and aftermarket car parts',
        services: [
          'Engine Components',
          'Suspension Parts',
          'Electrical Components',
          'Body Panels & Trims',
          'Wheels & Tires',
          'Interior Accessories'
        ]
      }
    }
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Manage your vehicles and service requests',
    vehicles: {
      title: 'My Vehicles',
      addVehicle: 'Add Vehicle',
      year: 'Year',
      status: 'Status',
      addNew: {
        title: 'Add New Vehicle',
        registrationFront: 'Registration Card (Front)',
        registrationBack: 'Registration Card (Back)',
        uploadFile: 'Upload a file',
        imageRequirements: 'PNG, JPG up to 5MB',
        uploading: 'Uploading...',
        errors: {
          fileTooLarge: 'File size must be less than 5MB',
          invalidFileType: 'Only image files are allowed',
          bothImagesRequired: 'Both front and back images are required'
        }
      }
    },
    serviceRequests: {
      title: 'Service Requests',
      newRequest: 'New Request',
      date: 'Date',
      description: 'Description',
      status: 'Status',
      quote: 'Quote',
      pending: 'Pending',
      vehicle: 'Vehicle',
      noVehicles: 'You need an approved vehicle to request service'
    }
  },
  footer: {
    about: {
      title: 'About Us',
      description: 'SollBridge is your trusted partner for all automotive needs in Dubai. We provide professional car care, maintenance, and repair services.'
    },
    contact: {
      title: 'Contact Us',
      location: 'Dubai, United Arab Emirates'
    },
    social: {
      title: 'Follow Us'
    },
    rights: 'All rights reserved.'
  }
};

const arabicTranslations = {
  common: {
    about: 'حول',
    services: 'الخدمات',
    signIn: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
    dashboard: 'لوحة التحكم',
    manage: 'إدارة',
    loading: 'جاري التحميل...',
    cancel: 'إلغاء',
    remove: 'إزالة',
    error: 'حدث خطأ',
    success: 'تمت العملية بنجاح',
    save: 'حفظ',
    bookNow: 'احجز الآن',
    english: 'English',
    arabic: 'العربية',
    russian: 'Русский',
    getStarted: 'ابدأ الآن',
    goToDashboard: 'الذهاب إلى لوحة التحكم',
    learnMore: 'اعرف المزيد',
    backTo: 'العودة إلى',
    allServices: 'جميع الخدمات',
    process: 'العملية',
    benefits: 'المميزات',
    overview: 'نظرة عامة',
    duration: 'المدة',
    warranty: 'الضمان',
    maintenance: 'الصيانة',
    serviceDetails: 'تفاصيل الخدمة'
  },
  profile: {
    myAccount: 'حسابي',
    title: 'معلومات الملف الشخصي',
    name: 'الاسم',
    dateOfBirth: 'تاريخ الميلاد',
    phoneNumbers: 'أرقام الهاتف',
    primary: 'الرئيسي',
    secondary: 'الثانوي',
    addSecondaryPhone: 'إضافة رقم هاتف ثانوي',
    removeSecondaryPhone: 'إزالة رقم الهاتف الثانوي',
    notSet: 'غير محدد',
    avatar: 'صورة الملف الشخصي',
    updateSuccess: 'تم تحديث ملفك الشخصي بنجاح.'
  },
  auth: {
    login: {
      title: 'تسجيل الدخول',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      rememberMe: 'تذكرني',
      forgotPassword: 'نسيت كلمة المرور؟',
      noAccount: 'ليس لديك حساب؟',
      signUp: 'إنشاء حساب',
      signingIn: 'جاري تسجيل الدخول...',
      resetInstructions: 'تحقق من بريدك الإلكتروني للحصول على تعليمات إعادة تعيين كلمة المرور'
    },
    register: {
      title: 'إنشاء حساب',
      firstName: 'الاسم الأول',
      lastName: 'اسم العائلة',
      username: 'اسم المستخدم',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      confirmPassword: 'تأكيد كلمة المرور',
      haveAccount: 'لديك حساب بالفعل؟',
      signIn: 'تسجيل الدخول',
      creatingAccount: 'جاري إنشاء الحساب...',
      passwordsDoNotMatch: 'كلمات المرور غير متطابقة',
      passwordTooWeak: 'كلمة المرور ضعيفة جداً',
      usernameTaken: 'اسم المستخدم مستخدم بالفعل',
      usernameRequirements: 'يجب أن يكون اسم المستخدم من 5 إلى 10 أحرف'
    }
  },
  home: {
    hero: {
      title: 'شريكك الموثوق لخدمات السيارات',
      subtitle: 'خدمات احترافية للعناية بالسيارات والصيانة في دبي',
      cta: 'استكشف خدماتنا'
    },
    steps: {
      title: 'كيف يعمل',
      step1: {
        title: 'الخطوة 1',
        heading: 'سجل سيارتك',
        description: 'أنشئ حساباً وسجل سيارتك معنا عن طريق تحميل بطاقة التسجيل.'
      },
      step2: {
        title: 'الخطوة 2',
        heading: 'اختر الخدمات',
        description: 'تصفح مجموعتنا الشاملة من الخدمات واختر ما تحتاجه.'
      },
      step3: {
        title: 'الخطوة 3',
        heading: 'احصل على الخدمة',
        description: 'احصل على خدمة احترافية من فريقنا المتخصص.'
      }
    },
    newsletter: {
      title: 'ابق على اطلاع',
      subtitle: 'اشترك في نشرتنا الإخبارية للحصول على آخر الأخبار والعروض',
      placeholder: 'أدخل بريدك الإلكتروني',
      button: 'اشترك',
      success: 'شكراً لاشتراكك في نشرتنا الإخبارية!'
    }
  },
  services: {
    title: 'خدماتنا',
    subtitle: 'خدمات سيارات احترافية مصممة لتلبية احتياجاتك',
    categories: {
      carCare: {
        title: 'العناية بالسيارات',
        description: 'خدمات العناية المتميزة بالسيارات تشمل PPF والطلاء السيراميك والتفصيل',
        services: [
          'فيلم حماية الطلاء (PPF)',
          'طلاء سيراميك',
          'تظليل النوافذ',
          'تفصيل',
          'تغليف الألوان',
          'ترميم المصابيح الأمامية',
          'ترميم داخلي وخارجي'
        ]
      },
      bodyWorks: {
        title: 'أعمال الهيكل',
        description: 'خدمات احترافية لأعمال الهيكل تشمل الطلاء وإصلاح الخدوش',
        services: [
          'طلاء وأعمال طلاء مخصصة',
          'إصلاح وتخصيص الجنوط',
          'إصلاح الخدوش',
          'إصلاح وتلميع الخدوش',
          'تعديلات قطع هيكل السيارة',
          'إزالة ومنع الصدأ'
        ]
      },
      mechanical: {
        title: 'الأعمال الميكانيكية',
        description: 'خدمات ميكانيكية متخصصة لأداء مثالي للسيارة',
        services: [
          'إصلاح ناقل الحركة والمحرك',
          'تبديل وتركيب القطع',
          'تحسينات الأداء والضبط',
          'إصلاحات وترقيات التعليق',
          'إصلاحات نظام الفرامل',
          'إصلاحات نظام التبريد',
          'إصلاحات كهربائية'
        ]
      },
      service: {
        title: 'الخدمة',
        description: 'عمليات الصيانة والخدمة المنتظمة',
        services: [
          'تغيير الزيت',
          'تغيير وتدوير الإطارات',
          'استبدال بطانات الفرامل',
          'فحص تشخيصي',
          'استبدال الفلتر',
          'تعبئة السوائل',
          'خدمة وتعبئة غاز التكييف'
        ]
      },
      parts: {
        title: 'قطع الغيار',
        description: 'قطع غيار أصلية وبديلة عالية الجودة',
        services: [
          'مكونات المحرك',
          'قطع التعليق',
          'المكونات الكهربائية',
          'ألواح وزخارف الهيكل',
          'العجلات والإطارات',
          'اكسسوارات داخلية'
        ]
      }
    }
  },
  dashboard: {
    title: 'لوحة التحكم',
    subtitle: 'إدارة سياراتك وطلبات الخدمة',
    vehicles: {
      title: 'سياراتي',
      addVehicle: 'إضافة سيارة',
      year: 'السنة',
      status: 'الحالة',
      addNew: {
        title: 'إضافة سيارة جديدة',
        registrationFront: 'بطاقة التسجيل (الأمام)',
        registrationBack: 'بطاقة التسجيل (الخلف)',
        uploadFile: 'تحميل ملف',
        imageRequirements: 'PNG, JPG حتى 5MB',
        uploading: 'جاري التحميل...',
        errors: {
          fileTooLarge: 'يجب أن يكون حجم الملف أقل من 5MB',
          invalidFileType: 'يسمح فقط بملفات الصور',
          bothImagesRequired: 'مطلوب صور الأمام والخلف'
        }
      }
    },
    serviceRequests: {
      title: 'طلبات الخدمة',
      newRequest: 'طلب جديد',
      date: 'التاريخ',
      description: 'الوصف',
      status: 'الحالة',
      quote: 'السعر',
      pending: 'قيد الانتظار',
      vehicle: 'السيارة',
      noVehicles: 'تحتاج إلى سيارة معتمدة لطلب الخدمة'
    }
  },
  footer: {
    about: {
      title: 'من نحن',
      description: 'سول بريدج هو شريكك الموثوق لجميع احتياجات السيارات في دبي. نقدم خدمات احترافية للعناية بالسيارات والصيانة والإصلاح.'
    },
    contact: {
      title: 'اتصل بنا',
      location: 'دبي، الإمارات العربية المتحدة'
    },
    social: {
      title: 'تابعنا'
    },
    rights: 'جميع الحقوق محفوظة.'
  }
};

const russianTranslations = {
  common: {
    about: 'О нас',
    services: 'Услуги',
    signIn: 'Войти',
    logout: 'Выйти',
    dashboard: 'Панель управления',
    manage: 'Управление',
    loading: 'Загрузка...',
    cancel: 'Отмена',
    remove: 'Удалить',
    error: 'Произошла ошибка',
    success: 'Операция выполнена успешно',
    save: 'Сохранить',
    bookNow: 'Забронировать',
    english: 'English',
    arabic: 'العربية',
    russian: 'Русский',
    getStarted: 'Начать сейчас',
    goToDashboard: 'Перейти в панель управления',
    learnMore: 'Узнать больше',
    backTo: 'Вернуться к',
    allServices: 'Все услуги',
    process: 'Процесс',
    benefits: 'Преимущества',
    overview: 'Обзор',
    duration: 'Длительность',
    warranty: 'Гарантия',
    maintenance: 'Обслуживание',
    serviceDetails: 'Детали услуги'
  },
  profile: {
    myAccount: 'мой аккаунт',
    title: 'Информация профиля',
    name: 'Имя',
    dateOfBirth: 'Дата рождения',
    phoneNumbers: 'Номера телефонов',
    primary: 'Основной',
    secondary: 'Дополнительный',
    addSecondaryPhone: 'Добавить дополнительный номер',
    removeSecondaryPhone: 'Удалить дополнительный номер',
    notSet: 'Не указано',
    avatar: 'Фото профиля',
    updateSuccess: 'Ваш профиль успешно обновлен.'
  },
  auth: {
    login: {
      title: 'Вход',
      email: 'Email',
      password: 'Пароль',
      rememberMe: 'Запомнить меня',
      forgotPassword: 'Забыли пароль?',
      noAccount: 'Нет аккаунта?',
      signUp: 'Зарегистрироваться',
      signingIn: 'Выполняется вход...',
      resetInstructions: 'Проверьте вашу почту для инструкций по сбросу пароля'
    },
    register: {
      title: 'Создать аккаунт',
      firstName: 'Имя',
      lastName: 'Фамилия',
      username: 'Имя пользователя',
      email: 'Email',
      password: 'Пароль',
      confirmPassword: 'Подтвердите пароль',
      haveAccount: 'Уже есть аккаунт?',
      signIn: 'Войти',
      creatingAccount: 'Создание аккаунта...',
      passwordsDoNotMatch: 'Пароли не совпадают',
      passwordTooWeak: 'Пароль слишком слабый',
      usernameTaken: 'Имя пользователя уже занято',
      usernameRequirements: 'Имя пользователя должно быть от 5 до 10 символов'
    }
  },
  home: {
    hero: {
      title: 'Ваш надежный партнер по обслуживанию автомобилей',
      subtitle: 'Профессиональный уход и обслуживание автомобилей в Дубае',
      cta: 'Изучить наши услуги'
    },
    steps: {
      title: 'Как это работает',
      step1: {
        title: 'Шаг 1',
        heading: 'Зарегистрируйте автомобиль',
        description: 'Создайте аккаунт и зарегистрируйте свой автомобиль, загрузив регистрационную карту.'
      },
      step2: {
        title: 'Шаг 2',
        heading: 'Выберите услуги',
        description: 'Просмотрите наш полный спектр услуг и выберите то, что вам нужно.'
      },
      step3: {
        title: 'Шаг 3',
        heading: 'Получите обслуживание',
        description: 'Получите профессиональное обслуживание от нашей команды экспертов.'
      }
    },
    newsletter: {
      title: 'Будьте в курсе',
      subtitle: 'Подпишитесь на нашу рассылку для получения последних новостей и предложений',
      placeholder: 'Введите ваш email',
      button: 'Подписаться',
      success: 'Спасибо за подписку на нашу рассылку!'
    }
  },
  services: {
    title: 'Наши услуги',
    subtitle: 'Профессиональные автомобильные услуги, адаптированные под ваши потребности',
    categories: {
      carCare: {
        title: 'Уход за автомобилем',
        description: 'Премиум услуги по уходу за автомобилем, включая PPF, керамическое покрытие и детейлинг',
        services: [
          'PPF (Защитная пленка)',
          'Керамическое покрытие',
          'Тонировка окон',
          'Детейлинг',
          'Цветная пленка',
          'Восстановление фар',
          'Восстановление салона и экстерьера'
        ]
      },
      bodyWorks: {
        title: 'Кузовные работы',
        description: 'Профессиональные кузовные работы, включая покраску и ремонт вмятин',
        services: [
          'Покраска и кастомная покраска',
          'Ремонт и кастомизация дисков',
          'Ремонт вмятин',
          'Ремонт царапин и полировка',
          'Регулировка кузовных деталей',
          'Удаление и предотвращение ржавчины'
        ]
      },
      mechanical: {
        title: 'Механические работы',
        description: 'Экспертные механические услуги для оптимальной производительности автомобиля',
        services: [
          'Ремонт трансмиссии и двигателя',
          'Замена и установка запчастей',
          'Тюнинг и улучшение производительности',
          'Ремонт и улучшение подвески',
          'Ремонт тормозной системы',
          'Ремонт системы охлаждения',
          'Ремонт электрики'
        ]
      },
      service: {
        title: 'Обслуживание',
        description: 'Регулярное техническое обслуживание',
        services: [
          'Замена масла',
          'Замена и ротация шин',
          'Замена тормозных колодок',
          'Диагностическая проверка',
          'Замена фильтров',
          'Доливка жидкостей',
          'Обслуживание и заправка кондиционера'
        ]
      },
      parts: {
        title: 'Запчасти',
        description: 'Качественные оригинальные и альтернативные автозапчасти',
        services: [
          'Компоненты двигателя',
          'Детали подвески',
          'Электрические компоненты',
          'Кузовные панели и молдинги',
          'Колеса и шины',
          'Аксессуары для салона'
        ]
      }
    }
  },
  dashboard: {
    title: 'Панель управления',
    subtitle: 'Управляйте вашими автомобилями и заявками на обслуживание',
    vehicles: {
      title: 'Мои автомобили',
      addVehicle: 'Добавить автомобиль',
      year: 'Год',
      status: 'Статус',
      addNew: {
        title: 'Добавить новый автомобиль',
        registrationFront: 'Регистрационная карта (Перед)',
        registrationBack: 'Регистрационная карта (Зад)',
        uploadFile: 'Загрузить файл',
        imageRequirements: 'PNG, JPG до 5MB',
        uploading: 'Загрузка...',
        errors: {
          fileTooLarge: 'Размер файла должен быть меньше 5MB',
          invalidFileType: 'Разрешены только изображения',
          bothImagesRequired: 'Требуются обе стороны регистрационной карты'
        }
      }
    },
    serviceRequests: {
      title: 'Заявки на обслуживание',
      newRequest: 'Новая заявка',
      date: 'Дата',
      description: 'Описание',
      status: 'Статус',
      quote: 'Стоимость',
      pending: 'В ожидании',
      vehicle: 'Автомобиль',
      noVehicles: 'Для заявки на обслуживание требуется одобренный автомобиль'
    }
  },
  footer: {
    about: {
      title: 'О нас',
      description: 'SollBridge - ваш надежный партнер для всех автомобильных потребностей в Дубае. Мы предоставляем профессиональные услуги по уходу, обслуживанию и ремонту автомобилей.'
    },
    contact: {
      title: 'Контакты',
      location: 'Дубай, Объединенные Арабские Эмираты'
    },
    social: {
      title: 'Следите за нами'
     },
    rights: 'Все права защищены.'
  }
};

// Single export for translations
export const translations: Translations = {
  en: englishTranslations,
  ar: arabicTranslations,
  ru: russianTranslations
};